import { resolveEffectivePackageTypeId } from "@src/common/utils/packageTypeDefaults";

export interface VehicleTypePackageVisibility {
  id: string;
  visiblePackageTypeIds?: string[] | null;
}

interface PackageItemTypeOverride {
  packageTypeId?: string | null;
}

/**
 * Distinct effective package types in play: shipment type plus each item's
 * effective type (override ?: shipment type).
 */
export function collectEffectivePackageTypeIds(
  shipmentPackageTypeId: string | null | undefined,
  packageItems: PackageItemTypeOverride[] | null | undefined,
): string[] {
  const ids = new Set<string>();
  if (shipmentPackageTypeId) {
    ids.add(shipmentPackageTypeId);
  }
  for (const item of packageItems ?? []) {
    const effectiveId: string | null = resolveEffectivePackageTypeId(
      item.packageTypeId,
      shipmentPackageTypeId,
    );
    if (effectiveId) {
      ids.add(effectiveId);
    }
  }
  return Array.from(ids);
}

/**
 * null / undefined visiblePackageTypeIds = allows all package types.
 * Otherwise every effective type must appear in the list.
 */
export function vehicleAllowsAllEffectivePackageTypes(
  vehicle: VehicleTypePackageVisibility,
  effectivePackageTypeIds: string[],
): boolean {
  if (effectivePackageTypeIds.length === 0) {
    return true;
  }
  if (!vehicle.visiblePackageTypeIds) {
    return true;
  }
  return effectivePackageTypeIds.every((id) =>
    vehicle.visiblePackageTypeIds!.includes(id),
  );
}

export function filterVehicleTypesByEffectivePackageTypes<
  T extends VehicleTypePackageVisibility,
>(vehicleTypes: T[], effectivePackageTypeIds: string[]): T[] {
  if (effectivePackageTypeIds.length === 0) {
    return vehicleTypes;
  }
  return vehicleTypes.filter((vehicle) =>
    vehicleAllowsAllEffectivePackageTypes(vehicle, effectivePackageTypeIds),
  );
}
